import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * Bandeau de mise à jour PWA (registerType 'prompt').
 *
 * À la détection d'une nouvelle version, on affiche un bandeau avec un compte à rebours
 * de 30 s qui recharge automatiquement (`updateSW(true)`) — sûr car la partie a une URL
 * restaurable (`/motus/m/<id>`) : après reload, le client re-rejoint la partie. Boutons
 * « Recharger maintenant » (immédiat) et « Plus tard » (annule le compteur).
 */
const COUNTDOWN_SECONDS = 30;

export function PwaUpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const updateSW = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    updateSW.current = registerSW({
      onNeedRefresh() {
        setSeconds(COUNTDOWN_SECONDS);
        setNeedRefresh(true);
      },
    });
  }, []);

  // Compte à rebours : recharge automatiquement à 0.
  useEffect(() => {
    if (!needRefresh) return;
    if (seconds <= 0) {
      void updateSW.current?.(true);
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [needRefresh, seconds]);

  if (!needRefresh) return null;

  const reloadNow = () => void updateSW.current?.(true);
  const dismiss = () => setNeedRefresh(false); // annule le compte à rebours

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed inset-x-4 bottom-4 z-[1400] mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-panda/40 bg-panda-ink/95 p-4 shadow-glow backdrop-blur sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-panda-cream">
        Nouvelle version disponible — rechargement dans{' '}
        <b className="font-mono text-panda">{seconds}s</b>
      </p>
      <div className="flex shrink-0 gap-2">
        <button
          onClick={reloadNow}
          className="rounded-full bg-panda px-4 py-1.5 text-sm font-bold text-panda-ink transition hover:brightness-110"
        >
          Recharger maintenant
        </button>
        <button
          onClick={dismiss}
          className="rounded-full px-4 py-1.5 text-sm font-semibold text-panda-cream/70 transition hover:text-panda-cream"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
}
